import React, { useState } from 'react';
import classNames from 'classnames';
import { SecondaryButton } from 'src/shared/components/button/Button';
import StatusIndicator from 'src/shared/components/status-indicator/StatusIndicator';
import { Access, Status } from 'src/shared/types';
import { useAppDispatch, useAppSelector } from 'src/store';
import { Info as InfoOutlinedIcon, Mail as EmailOutlinedIcon, Phone as PhoneIcon, Pencil } from 'lucide-react';

import { batch } from 'react-redux';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import StatusChip from 'src/shared/components/status-chip/StatusChip';
import { getUserStatusFromEnum } from 'src/app/admin/views/user-management/userManagementHelpers';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import usePermissions from 'src/shared/hooks/usePermissions';
import { useResendInvitationEmailMutation } from '../../../state/api/userManagementGraphSlice';
import { getEditProjectUserFormState } from '../../../state/userManagementSelector';
import { setEditUserFormMode, setEditUserFormState, setEditUserView } from '../../../state/userManagementSlice';
import styles from '../EditProjectUser.module.scss';

type EditProjectUserHeaderProps = {
    setErrorMessage: (message: string) => void;
    /** When true (profile step), hide the pencil that would navigate to `edit-user-details` again */
    hideEditLink?: boolean;
    /** Shown under “Assigned to this org” (e.g. Deactivate / Unassign) */
    assignedOrgActions?: React.ReactNode;
};

/** Identity card: name, status, contact, org assignment indicator, invitation resend for Invited users */
const EditProjectUserHeader = ({ setErrorMessage, hideEditLink, assignedOrgActions }: EditProjectUserHeaderProps) => {
    const dispatch = useAppDispatch();
    const formState = useAppSelector(getEditProjectUserFormState);
    const { firstName, lastName, email, status, phone, canResendEmailInvitation } = formState;
    const [isSetPasswordChecked, setIsSetPasswordChecked] = useState(false);
    const [password, setPassword] = useState('');
    const [passwordValidation, setPasswordValidation] = useState({
        minLength: false,
        hasNumber: false,
        hasSpecialChar: false,
        hasUpperCase: false,
        hasLowerCase: false,
    });
    const { currentCustomerId } = useCustomer();
    const userNameClass = classNames(styles.name, styles['name' + status]);
    const [resendInvitationEmail] = useResendInvitationEmailMutation();
    const { isCurrentUserInternal } = usePermissions();
    const isAssignedToCustomer = formState.customers.some(customer => customer.customerId === currentCustomerId && customer.active);
    const isActiveOrInvited = status === 'Active' || status === 'Invited';
    const shouldShowSetPasswordControls = isCurrentUserInternal;

    const validatePassword = (pwd: string) => {
        const validation = {
            minLength: pwd.length >= 8,
            hasNumber: /\d/.test(pwd),
            hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd),
            hasUpperCase: /[A-Z]/.test(pwd),
            hasLowerCase: /[a-z]/.test(pwd),
        };
        setPasswordValidation(validation);
        return validation;
    };

    const isPasswordValid = () => {
        return Object.values(passwordValidation).every(v => v === true);
    };

    const isResendDisabled = !canResendEmailInvitation || (shouldShowSetPasswordControls && isSetPasswordChecked && !isPasswordValid());

    /** Mirrors API outcome into form state so the resend button can show “already sent” */
    const setResendFlag = (flag: boolean) => {
        dispatch(
            setEditUserFormState({
                canResendEmailInvitation: flag,
            })
        );
    };
    const handleResendEmail = async () => {
        try {
            await resendInvitationEmail({
                email: email,
                password: shouldShowSetPasswordControls && isSetPasswordChecked ? password : '',
            }).unwrap();
            setResendFlag(false);
            if (isSetPasswordChecked) {
                setPassword('');
                setIsSetPasswordChecked(false);
                setPasswordValidation({
                    minLength: false,
                    hasNumber: false,
                    hasSpecialChar: false,
                    hasUpperCase: false,
                    hasLowerCase: false,
                });
            }
        } catch (error) {
            console.error('rejected', error);
            setErrorMessage('Unable to resend the invitation email. Please try again later.');
        }
    };

    return (
        <div className={styles.editProjectUserHeader}>
            <div className={styles.headerCard}>
                <div className={styles.identityRow}>
                    <div className={styles.avatar} aria-hidden>
                        {`${firstName[0] ?? ''}${lastName[0] ?? ''}` || '—'}
                    </div>
                    <div className={styles.identityBody}>
                        <div className={styles.nameStatusRow}>
                            <div className={styles.nameRow}>
                                <div className={userNameClass}>{`${firstName} ${lastName}`.trim() || 'User'}</div>
                                {isActiveOrInvited && !hideEditLink ? (
                                    <button
                                        type='button'
                                        className={styles.editNameIconButton}
                                        aria-label='Edit user details'
                                        onClick={() => {
                                            batch(() => {
                                                dispatch(setEditUserFormMode(Access.UPDATE));
                                                dispatch(setEditUserView('edit-user-details'));
                                            });
                                        }}
                                    >
                                        <Pencil className={styles.editNameIcon} aria-hidden />
                                    </button>
                                ) : null}
                            </div>
                            <StatusChip label={status} status={getUserStatusFromEnum(status)} />
                        </div>
                        <div className={styles.contactLines}>
                            <div className={styles.contactLine}>
                                <EmailOutlinedIcon className={styles.contactLineIcon} aria-hidden />
                                <span className={styles.contactLineText} data-testid='user-management-user-detail-email'>
                                    {email}
                                </span>
                            </div>
                            {phone ? (
                                <div className={styles.contactLine}>
                                    <PhoneIcon className={styles.contactLineIcon} aria-hidden />
                                    <span className={styles.contactLineText} data-testid='phone'>
                                        {phone}
                                    </span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className={styles.metaToolbar}>
                    <div className={styles.orgAssignmentMeta}>
                        <StatusIndicator
                            status={isAssignedToCustomer ? Status.SUCCESS : isActiveOrInvited ? Status.WARNING : Status.ERROR}
                            statusText={
                                isAssignedToCustomer
                                    ? 'Assigned to this org'
                                    : isActiveOrInvited
                                    ? 'Invited to this org'
                                    : 'Unassigned from this org'
                            }
                        />
                        {isAssignedToCustomer && assignedOrgActions ? (
                            <div className={styles.footerInlineActions} data-testid='edit-project-user-org-actions'>
                                {assignedOrgActions}
                            </div>
                        ) : null}
                    </div>
                </div>

                {status === 'Invited' ? (
                    <div className={styles.invitationPanel}>
                        <div className={styles.invitationPanelMessage}>
                            <InfoOutlinedIcon className={styles.invitationPanelIcon} aria-hidden />
                            <span>This user has not accepted their email invitation.</span>
                        </div>
                        <div className={styles.invitationPanelControlRow}>
                            {shouldShowSetPasswordControls ? (
                                <label className={styles.setPasswordControl}>
                                    <input
                                        type='checkbox'
                                        checked={isSetPasswordChecked}
                                        onChange={event => {
                                            const isChecked = event.target.checked;
                                            setIsSetPasswordChecked(isChecked);

                                            if (!isChecked) {
                                                setPassword('');
                                                setPasswordValidation({
                                                    minLength: false,
                                                    hasNumber: false,
                                                    hasSpecialChar: false,
                                                    hasUpperCase: false,
                                                    hasLowerCase: false,
                                                });
                                            }
                                        }}
                                    />
                                    <span>Set password</span>
                                    <QuestionButtonHelp
                                        type='custom'
                                        placement='top'
                                        zIndex={1400}
                                        maxWidth={300}
                                        helpText={
                                            <p>
                                                A manually set password will be included in the invitation email. If no password is set, an
                                                auto-generated password will be sent.
                                            </p>
                                        }
                                    />
                                </label>
                            ) : null}
                            {shouldShowSetPasswordControls && isSetPasswordChecked ? (
                                <div className={styles.setPasswordInputWrapper}>
                                    <input
                                        type='text'
                                        className={styles.setPasswordInput}
                                        value={password}
                                        onChange={event => {
                                            const pwd = event.target.value;
                                            setPassword(pwd);
                                            validatePassword(pwd);
                                        }}
                                        placeholder='Enter Password'
                                    />
                                    <div className={styles.passwordRequirements}>
                                        <p>Password must contain at least:</p>
                                        <ul>
                                            <li className={passwordValidation.minLength ? styles.met : ''}>8 character(s)</li>
                                            <li className={passwordValidation.hasNumber ? styles.met : ''}>1 number</li>
                                            <li className={passwordValidation.hasSpecialChar ? styles.met : ''}>1 special character</li>
                                            <li className={passwordValidation.hasUpperCase ? styles.met : ''}>1 uppercase letter</li>
                                            <li className={passwordValidation.hasLowerCase ? styles.met : ''}>1 lowercase letter</li>
                                        </ul>
                                    </div>
                                </div>
                            ) : null}
                            <SecondaryButton onClick={handleResendEmail} disabled={isResendDisabled}>
                                <EmailOutlinedIcon />
                                &nbsp;
                                {!canResendEmailInvitation ? 'Email invitation resent' : 'Resend email invitation'}
                            </SecondaryButton>
                        </div>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default EditProjectUserHeader;
